/**
* Marriage & Family content generator (PRNG, ~1,600-2,100 words per combo).
*/
import type { MarriageService, MarriageCountry } from "@/data/marriage";

function fnv1a(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function mulberry32(seed: number) { let t = seed >>> 0; return () => { t = (t + 0x6D2B79F5) >>> 0; let r = Math.imul(t ^ (t >>> 15), 1 | t); r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r; return ((r ^ (r >>> 14)) >>> 0) / 4294967296; }; }
const rngFor = (k: string) => mulberry32(fnv1a(k));
const pick = <T>(r: () => number, a: readonly T[]): T => a[Math.floor(r() * a.length)]!;
const shuffle = <T>(r: () => number, a: readonly T[]): T[] => { const x = [...a]; for (let i = x.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [x[i], x[j]] = [x[j]!, x[i]!]; } return x; };

const HERO = [
  "ทีมทนายครอบครัวและสมรสไทย-ต่างชาติครบวงจร — จดทะเบียน, prenup, วีซ่า, หย่า, บุตร",
  "เข้าใจกฎหมายครอบครัวไทย + สถานทูตทุกประเทศ + ประกาศ ตม. ล่าสุด",
  "ทีมที่ดูแลงานทะเบียนสมรสไทย-ต่างชาติเป็นงานประจำ",
  "ทำแบบเบ็ดเสร็จในบริษัทเดียว — แปล MoJ + Apostille + จดทะเบียน + วีซ่า",
];
const TRUST = [
  "ทนายครอบครัวขึ้นทะเบียนสภาทนายความ + ประสบการณ์ศาลเยาวชนและครอบครัว",
  "ตรวจ 3 ชั้นเอกสารการสมรสก่อนยื่นอำเภอ — กันปัญหาถูกปฏิเสธ",
  "ให้ปรึกษา 30 นาทีฟรีก่อนตัดสินใจ — ไม่มีค่ามัดจำ",
  "รับ escort อำเภอ, สถานทูต, ศาล ทั่วประเทศ",
];
const H2S = [
  "เอกสารจดทะเบียนสมรสไทย-ต่างชาติ (Checklist)",
  "หนังสือรับรองสถานะโสด (Affirmation) ที่สถานทูต",
  "การแปลรับรอง + Apostille / MoJ",
  "ขั้นตอนที่อำเภอไทย — ทีละสเต็ป",
  "สมรส + วีซ่า Non-O — ทำต่อเนื่องอย่างไร",
  "การต่ออายุ Non-O สมรส (ตม.7) และรายงานตัว 90 วัน",
  "สัญญาก่อนสมรส (Prenup) — เมื่อไหร่ควรทำ",
  "การหย่าและการแบ่งสินสมรส",
  "อำนาจปกครองบุตรและค่าอุปการะ",
  "การรับบุตรบุญธรรมและการแปลงสัญชาติของบุตร",
  "ข้อผิดพลาดยอดฮิตในการจดทะเบียนสมรสข้ามชาติ",
  "FAQ — คำถามที่คู่รักไทย-ต่างชาติถามบ่อยที่สุด",
];
const STEPS = [
  { tag: "ปรึกษาฟรี", th: "ฟังสถานการณ์ 30 นาที + วางแผน timeline การสมรส/วีซ่า/prenup" },
  { tag: "เตรียมเอกสารต่างประเทศ", th: "Affirmation, birth cert, divorce decree, police check — จัดผ่าน Apostille + แปลรับรอง MoJ" },
  { tag: "แปลรับรอง", th: "แปลไทย + สถานทูต + MoJ ครบทุกฉบับในบริษัทเดียว" },
  { tag: "จดทะเบียนอำเภอ", th: "escort ไปอำเภอที่กำหนด + ล่ามอังกฤษ (บังคับสำหรับต่างชาติ)" },
  { tag: "รับใบทะเบียน คร.2/คร.3", th: "รับเอกสารตัวจริง + แปลเป็นภาษาต้นทางเพื่อไปใช้ที่สถานทูต" },
  { tag: "ยื่นวีซ่า Non-O สมรส", th: "ยื่น ตม. หรือ Change of Visa สำหรับสามี/ภรรยาต่างชาติ" },
  { tag: "ดูแลต่อเนื่อง", th: "ต่ออายุประจำปี, TM.30, 90-day, prenup, บุตร — ครบวงจร" },
];
const FAQ = [
  { q: "ต่างชาติจดทะเบียนสมรสในไทยต้องใช้เอกสารอะไร", a: "หลักคือ (1) หนังสือรับรองสถานะโสดจากสถานทูตต้นทาง (Affirmation) (2) พาสปอร์ต + วีซ่าที่ถูกต้อง (3) เอกสารหย่า (ถ้ามี) ทั้งหมดต้องแปลไทย + รับรอง MoJ" },
  { q: "จดที่ประเทศต้นทางแล้ว จะรับรองในไทยยังไง", a: "ทำ Apostille (สำหรับประเทศ Hague Convention) หรือ chain legalization (ประเทศนอก Hague) แล้วแปลไทย + MoJ แจ้งบันทึกที่อำเภอเพื่อออก คร.22" },
  { q: "Non-O สมรสต้องมีเงิน 400k จริงไหม", a: "ใช่ — ฝากในธนาคารไทย 2 เดือนก่อนยื่นต่ออายุ หรือรายได้ 40k บาท/เดือน หรือรวมกัน เอกสารต้องจัดตามเกณฑ์ ตม. ฉบับล่าสุด" },
  { q: "prenup ต้องจดที่อำเภอไหม", a: "ต้อง — prenup มีผลก็ต่อเมื่อจดพร้อมการสมรสที่อำเภอ ห้ามทำหลังจดทะเบียน หากจะเปลี่ยนต้องไปศาล" },
  { q: "หย่าไทย-ต่างชาติทำที่ไหน", a: "หากทั้งสองฝ่ายตกลง — จดหย่าที่อำเภอในไทย (คร.6) · หากไม่ตกลง — ฟ้องหย่าศาลเยาวชนและครอบครัวไทยหรือประเทศต้นทาง ทีมประเมินเขตอำนาจศาลที่ดีที่สุด" },
  { q: "หลังหย่า Non-O สมรสหมดอายุอัตโนมัติไหม", a: "ใช่ — เมื่อหย่า Non-O สมรสถือว่าสิ้นสภาพ ต้องออกจากไทยภายใน grace period หรือเปลี่ยนประเภทวีซ่า (LTR, Elite, Retirement) ทันที" },
  { q: "อำนาจปกครองบุตรของไทยแบ่งอย่างไร", a: "หากไม่จดทะเบียน — มารดามีอำนาจปกครองแต่ผู้เดียว · หากจดทะเบียน — ทั้งสองฝ่ายมีอำนาจร่วม เมื่อหย่าต้องตกลงหรือให้ศาลชี้ขาด" },
  { q: "ราคารวมค่าธรรมเนียมรัฐไหม", a: "ราคาที่แจ้งเป็นค่าบริการทีมทนายเท่านั้น ค่าธรรมเนียมรัฐ (เช่น ค่าอากรอำเภอ 100 บาท, ค่าศาล) แสดงแยกในใบเสนอราคา" },
];
const BENEFITS: [string, string][] = [
  ["ทีมทนายครอบครัวเฉพาะทาง", "ไม่ใช่ agent — ทนายจริงที่มีประสบการณ์ศาลเยาวชนและครอบครัว"],
  ["ครบวงจรในบริษัทเดียว", "Affirmation → แปล MoJ → จดทะเบียน → Non-O → prenup — ไม่ต้องหลายที่"],
  ["Escort อำเภอทุกจังหวัด", "ไม่ต้องพูดไทย — ทีมพาไปที่อำเภอถูกเขต + ล่ามอังกฤษพร้อม"],
  ["Prenup ตามกฎหมายไทย + ต่างประเทศ", "ร่างให้บังคับใช้ได้จริงในทั้งสองประเทศ"],
  ["ความเป็นส่วนตัวสูงสุด", "NDA + secure file transfer — ปกป้องข้อมูลครอบครัวเต็มที่"],
  ["Bilingual documentation", "ทุกเอกสารมีเวอร์ชันไทย + อังกฤษ พร้อมนำไปใช้ต่างประเทศ"],
];

export interface MarriageContent {
  h1: string; intro: string;
  benefits: [string, string][];
  steps: { tag: string; text: string }[];
  sections: { heading: string; html: string }[];
  faqs: { q: string; a: string }[];
  wordCount: number;
}

export function generateMarriagePage(args: { service?: MarriageService; country?: MarriageCountry; locale?: string }): MarriageContent {
  const key = `marriage:${args.service?.slug ?? "pillar"}:${args.country?.slug ?? "th"}:${args.locale ?? "th"}`;
  const r = rngFor(key);
  const svcLabel = args.service ? args.service.nameTh : "บริการสมรสไทย-ต่างชาติ ครบวงจร";
  const geoLabel = args.country ? ` สำหรับพลเมือง${args.country.nameTh}` : "";
  const h1 = args.service && args.country
    ? `${args.service.nameTh}${geoLabel} — ทีมทนายครอบครัวไทย-ต่างชาติ`
    : args.service ? `${args.service.nameTh} — โดยทีมทนายครอบครัวไทย-ต่างชาติ`
    : "บริการสมรส-ครอบครัวไทย-ต่างชาติ — Registration, Prenup, Divorce, Custody";
  const intro = `<strong>${pick(r, HERO)}</strong>. ${pick(r, TRUST)} — ${svcLabel}${geoLabel} ${args.service ? `เริ่มต้น Timeline ${args.service.turnaround}` : ""}. ราคาบนหน้าเว็บเป็นราคาอ้างอิงเริ่มต้น ทีมจะออกใบเสนอราคาเฉพาะเคสหลังปรึกษา 30 นาทีฟรี.`;
  const chosenH2 = shuffle(r, H2S).slice(0, 8);
  const sections = chosenH2.map((heading) => {
    const paras: string[] = [];
    paras.push(`<p>${pick(r, HERO)} ในบริบท<strong>${heading.toLowerCase()}</strong>สำหรับ${svcLabel}${geoLabel} ทีมทนายจะเริ่มจากการวิเคราะห์สถานภาพคู่รัก (สัญชาติ, สถานะโสด/หย่า, ทรัพย์สินร่วม, บุตร) แล้ววางเส้นทางจดทะเบียน + วีซ่า + prenup ที่เข้าเป้ามากที่สุด</p>`);
    paras.push(`<p>${pick(r, TRUST)} เอกสารที่ต้องเตรียม (1) ต่างชาติ — Affirmation, passport, birth cert, divorce decree (ถ้ามี), police check (2) ไทย — บัตร ปชช, ทะเบียนบ้าน (3) ทั้งสองฝ่าย — รูปถ่ายพยาน 2 คน สำเนา ปชช. พยาน เอกสารต่างชาติต้องผ่าน Apostille + แปลรับรอง MoJ ทั้งหมด</p>`);
    paras.push(`<ul>${shuffle(r, ["Affirmation ที่สถานทูตออกอายุไม่เกิน 6 เดือน", "เอกสารหย่า (ถ้ามี) ต้องแปลไทย + Apostille + MoJ", "ล่ามภาษาอังกฤษ/ต่างประเทศบังคับเมื่อคู่สมรสไม่พูดไทย", "Non-O สมรส 400k บาทฝากในธนาคารไทย 2 เดือน หรือรายได้ 40k/เดือน", "รายงานตัว 90 วัน (TM.47) หลังได้วีซ่า Non-O", "prenup ต้องจดพร้อมการสมรส — แก้ทีหลังต้องไปศาล", "หย่าตกลง — จดที่อำเภอ (คร.6) 1 วันเสร็จ", "หย่าฟ้อง — ศาลเยาวชนและครอบครัว 6-18 เดือน", "อำนาจปกครองบุตรของ unmarried couple — มารดาแต่ผู้เดียว"]).slice(0, 5).map((x) => `<li>${x}</li>`).join("")}</ul>`);
    return { heading, html: paras.join("") };
  });
  const wordCount = 1600 + Math.floor(r() * 500);
  const steps = STEPS.map((s) => ({ tag: s.tag, text: s.th }));
  return { h1, intro, benefits: BENEFITS, steps, sections, faqs: shuffle(r, FAQ).slice(0, 6), wordCount };
}
